import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  UserPlus,
  UserMinus,
  Calendar,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Users,
  FileText,
  Plus,
  Eye,
  Edit,
  MessageSquare,
  Star,
  Trash2,
  Loader2,
  X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Progress } from '../ui/progress';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { onboardingApi, employeeApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../contexts/AuthContext';
import { useManagerViewPermission } from '../../hooks/useManagerViewPermission';
import { cn } from '../../lib/utils';

interface OnboardingFormData {
  employee_id: string;
  process_type: 'ONBOARDING' | 'OFFBOARDING';
  start_date: string;
  expected_completion_date: string;
  assigned_to: string;
  buddy_id?: string;
  status: string;
  current_stage: string;
  notes?: string;
  reason?: string;
  last_working_day?: string;
}

interface BuddyFormData {
  onboardee_id: string;
  buddy_id: string;
  duration: number;
  notes?: string;
}

const initialOnboardingForm: OnboardingFormData = {
  employee_id: '',
  process_type: 'ONBOARDING',
  start_date: new Date().toISOString().split('T')[0],
  expected_completion_date: '',
  assigned_to: '',
  buddy_id: '',
  status: 'NOT_STARTED',
  current_stage: 'Documentation',
  notes: '',
  reason: '',
  last_working_day: '',
};

const initialBuddyForm: BuddyFormData = {
  onboardee_id: '',
  buddy_id: '',
  duration: 30,
  notes: '',
};

const OnboardingOffboarding: React.FC = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();
  const currentUserId = user?.id;
  const { isUserView, canDoOnboardingActions, canEditDeleteOnboarding, canViewOffboardingDetails, canViewAllBuddyAssignments } = useManagerViewPermission();
  const [selectedTab, setSelectedTab] = useState('dashboard');

  // Dialog states
  const [isOnboardingDialogOpen, setIsOnboardingDialogOpen] = useState(false);
  const [isOffboardingDialogOpen, setIsOffboardingDialogOpen] = useState(false);
  const [isBuddyDialogOpen, setIsBuddyDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false);

  // Form states
  const [onboardingForm, setOnboardingForm] = useState<OnboardingFormData>(initialOnboardingForm);
  const [buddyForm, setBuddyForm] = useState<BuddyFormData>(initialBuddyForm);
  const [editingProcess, setEditingProcess] = useState<any>(null);
  const [viewingProcess, setViewingProcess] = useState<any>(null);
  const [viewingBuddy, setViewingBuddy] = useState<any>(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackRating, setFeedbackRating] = useState(5);

  // Fetch onboarding processes
  const { data: onboardingProcesses = [], isLoading: onboardingLoading } = useQuery({
    queryKey: ['onboardingProcesses'],
    queryFn: () => onboardingApi.processes.getAll(),
  });

  // Fetch employees
  const { data: employees = [] } = useQuery({
    queryKey: ['employees'],
    queryFn: () => employeeApi.getAll({}),
  });

  // Filter processes by type
  const onboardingList = onboardingProcesses.filter((p: any) => p.process_type === 'ONBOARDING' || !p.process_type);
  const offboardingList = onboardingProcesses.filter((p: any) => p.process_type === 'OFFBOARDING');
  const buddyPrograms = !canViewAllBuddyAssignments 
    ? onboardingProcesses.filter((p: any) => p.buddy_id && (p.buddy_id === currentUserId || p.employee_id === currentUserId))
    : onboardingProcesses.filter((p: any) => p.buddy_id);

  // Stats
  const activeOnboarding = onboardingList.filter((p: any) => p.status === 'IN_PROGRESS' || p.status === 'NOT_STARTED').length;
  const activeOffboarding = offboardingList.filter((p: any) => p.status === 'IN_PROGRESS' || p.status === 'NOT_STARTED').length;
  const completedOnboarding = onboardingList.filter((p: any) => p.status === 'COMPLETED').length;
  const activeBuddyPrograms = buddyPrograms.filter((p: any) => p.status !== 'COMPLETED').length;

  // Mutations
  const createProcessMutation = useMutation({
    mutationFn: onboardingApi.processes.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboardingProcesses'] });
      setIsOnboardingDialogOpen(false);
      setIsOffboardingDialogOpen(false);
      setOnboardingForm(initialOnboardingForm);
      toast({ title: 'Process created successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const updateProcessMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => onboardingApi.processes.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboardingProcesses'] });
      setEditingProcess(null);
      setIsViewDialogOpen(false);
      toast({ title: 'Process updated successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const deleteProcessMutation = useMutation({
    mutationFn: onboardingApi.processes.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboardingProcesses'] });
      toast({ title: 'Process deleted successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Handlers
  const handleCreateOnboarding = () => {
    if (!onboardingForm.employee_id || !onboardingForm.expected_completion_date) {
      toast({ title: 'Error', description: 'Please fill required fields', variant: 'destructive' });
      return;
    }
    const cleanData = Object.fromEntries(
      Object.entries({
        ...onboardingForm,
        process_type: 'ONBOARDING',
        completion_percentage: 0,
      }).map(([key, value]) => [key, value === '' ? null : value])
    );
    createProcessMutation.mutate(cleanData);
  };

  const handleCreateOffboarding = () => {
    if (!onboardingForm.employee_id || !onboardingForm.last_working_day) {
      toast({ title: 'Error', description: 'Please fill required fields', variant: 'destructive' });
      return;
    }
    const cleanData = Object.fromEntries(
      Object.entries({
        ...onboardingForm,
        process_type: 'OFFBOARDING',
        completion_percentage: 0,
      }).map(([key, value]) => [key, value === '' ? null : value])
    );
    createProcessMutation.mutate(cleanData);
  };

  const handleAssignBuddy = () => {
    if (!buddyForm.onboardee_id || !buddyForm.buddy_id) {
      toast({ title: 'Error', description: 'Please select both onboardee and buddy', variant: 'destructive' });
      return;
    }
    if (buddyForm.onboardee_id === buddyForm.buddy_id) {
      toast({ title: 'Error', description: 'Onboardee and buddy cannot be the same person', variant: 'destructive' });
      return;
    }
    const process = editingProcess || onboardingList.find((p: any) => p.employee_id === buddyForm.onboardee_id);
    if (process) {
      updateProcessMutation.mutate({
        id: process.id,
        data: { 
          buddy_id: buddyForm.buddy_id, 
          notes: buddyForm.notes,
          expected_completion_date: new Date(new Date(process.start_date || Date.now()).getTime() + buddyForm.duration * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
        },
      });
    } else {
      createProcessMutation.mutate({
        employee_id: buddyForm.onboardee_id,
        buddy_id: buddyForm.buddy_id,
        process_type: 'ONBOARDING',
        start_date: new Date().toISOString().split('T')[0],
        expected_completion_date: new Date(Date.now() + buddyForm.duration * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        status: 'IN_PROGRESS',
        current_stage: 'Buddy Program',
        completion_percentage: 0,
        notes: buddyForm.notes,
      });
    }
    setIsBuddyDialogOpen(false);
    setBuddyForm(initialBuddyForm);
    setEditingProcess(null);
  };

  const handleViewProcess = (process: any) => {
    setViewingProcess(process);
    setIsViewDialogOpen(true);
  };

  const handleEditProcess = (process: any) => {
    setEditingProcess(process);
    setOnboardingForm({
      employee_id: process.employee_id,
      process_type: process.process_type || 'ONBOARDING',
      start_date: process.start_date || '',
      expected_completion_date: process.expected_completion_date || '',
      assigned_to: process.assigned_to || '',
      buddy_id: process.buddy_id || '',
      status: process.status,
      current_stage: process.current_stage || '',
      notes: process.notes || '',
      reason: process.reason || '',
      last_working_day: process.last_working_day || '',
    });
    if (process.process_type === 'OFFBOARDING') {
      setIsOffboardingDialogOpen(true);
    } else {
      setIsOnboardingDialogOpen(true);
    }
  };

  const getCompletionPercentageForStatus = (status: string, currentPercentage?: number): number => {
    switch (status) {
      case 'COMPLETED':
        return 100;
      case 'NOT_STARTED':
        return 0;
      case 'IN_PROGRESS':
        return currentPercentage && currentPercentage > 0 ? currentPercentage : 50;
      case 'DELAYED':
        return currentPercentage || 0;
      default:
        return currentPercentage || 0;
    }
  };

  const handleUpdateProcess = () => {
    if (!editingProcess) return;
    const cleanData = Object.fromEntries(
      Object.entries(onboardingForm).map(([key, value]) => [key, value === '' ? null : value])
    );

    const completionPercentage = getCompletionPercentageForStatus(
      onboardingForm.status,
      editingProcess.completion_percentage
    );

    updateProcessMutation.mutate({
      id: editingProcess.id,
      data: { ...cleanData, completion_percentage: completionPercentage },
    });
    setEditingProcess(null);
    setIsOnboardingDialogOpen(false);
    setIsOffboardingDialogOpen(false);
  };

  const handleDeleteProcess = (id: string) => {
    if (confirm('Are you sure you want to delete this process?')) {
      deleteProcessMutation.mutate(id);
    }
  };

  const handleViewBuddy = (process: any) => {
    setViewingBuddy(process);
    setIsViewDialogOpen(true);
  };

  const handleEditBuddy = (program: any) => {
    setEditingProcess(program);
    setBuddyForm({
      onboardee_id: program.employee_id || '',
      buddy_id: program.buddy_id || '',
      duration: program.expected_completion_date ? 
        Math.max(1, Math.round((new Date(program.expected_completion_date).getTime() - new Date(program.start_date || Date.now()).getTime()) / (1000 * 60 * 60 * 24))) : 30,
      notes: program.notes || '',
    });
    setIsBuddyDialogOpen(true);
  };

  const handleFeedback = (process: any) => {
    setViewingBuddy(process);
    setIsFeedbackDialogOpen(true);
  };

  const handleSubmitFeedback = () => {
    if (!viewingBuddy) return;
    updateProcessMutation.mutate({
      id: viewingBuddy.id,
      data: {
        notes: `${viewingBuddy.notes || ''}\n\nFeedback (${feedbackRating}/5): ${feedbackText}`,
      },
    });
    setIsFeedbackDialogOpen(false);
    setFeedbackText('');
    setFeedbackRating(5);
  };

  const getEmployeeName = (employeeId: string): string => {
    const employee = employees.find((emp: any) => emp.id === employeeId);
    if (employee) {
      return `${employee.first_name || ''} ${employee.last_name || ''}`.trim() || 'Unknown Employee';
    }
    return 'Unknown Employee';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED': return 'bg-emerald-50 text-emerald-700 border-emerald-200/50 dark:bg-emerald-950/20 dark:text-emerald-400';
      case 'IN_PROGRESS': return 'bg-blue-50 text-blue-700 border-blue-200/50 dark:bg-blue-900/20 dark:text-blue-400';
      case 'DELAYED': return 'bg-rose-50 text-rose-700 border-rose-200/50 dark:bg-rose-900/20 dark:text-rose-400';
      case 'NOT_STARTED': return 'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-400';
      default: return 'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-400';
    }
  };

  const getDisplayProgress = (process: any): number => {
    if (process.status === 'COMPLETED') {
      return 100;
    }
    if (process.status === 'NOT_STARTED') {
      return 0;
    }
    return process.completion_percentage || (process.status === 'IN_PROGRESS' ? 50 : 0);
  };

  if (onboardingLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2 mt-3 font-bold text-xs text-slate-400">Loading lifecycle processes...</span>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto space-y-8 p-6 custom-scrollbar animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">Manage employee lifecycle processes</h2>
        </div>
        {!isUserView && canDoOnboardingActions && (
          <div className="flex items-center space-x-3 shrink-0 self-start md:self-auto">
            <Button 
              variant="outline"
              onClick={() => { setEditingProcess(null); setOnboardingForm({ ...initialOnboardingForm, process_type: 'OFFBOARDING' }); setIsOffboardingDialogOpen(true); }}
            >
              <UserMinus className="h-4 w-4 shrink-0 mr-1.5" />
              <span>Start Offboarding</span>
            </Button>
            <Button 
              onClick={() => { setEditingProcess(null); setOnboardingForm(initialOnboardingForm); setIsOnboardingDialogOpen(true); }}
            >
              <UserPlus className="h-4 w-4 shrink-0 mr-1.5" />
              <span>Start Onboarding</span>
            </Button>
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border border-slate-105 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 rounded-2xl overflow-hidden">
          <CardContent className="pt-6 px-6 pb-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-bold text-slate-400 tracking-wider">Active Onboarding</p>
                <p className="text-3xl font-bold text-[#143A62] dark:text-slate-100 mt-2">{activeOnboarding}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-950/30 flex items-center justify-center shrink-0 shadow-sm border border-blue-100/30">
                <UserPlus className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-105 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 rounded-2xl overflow-hidden">
          <CardContent className="pt-6 px-6 pb-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-bold text-slate-400 tracking-wider">Active Offboarding</p>
                <p className="text-3xl font-bold text-orange-600 mt-2">{activeOffboarding}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-orange-50 dark:bg-orange-950/30 flex items-center justify-center shrink-0 shadow-sm border border-orange-100/30">
                <UserMinus className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-105 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 rounded-2xl overflow-hidden">
          <CardContent className="pt-6 px-6 pb-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-bold text-slate-400 tracking-wider">Completed This Month</p>
                <p className="text-3xl font-bold text-green-600 mt-2">{completedOnboarding}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-emerald-50 dark:bg-emerald-950/30 flex items-center justify-center shrink-0 shadow-sm border border-emerald-100/30">
                <CheckCircle2 className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-105 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 rounded-2xl overflow-hidden">
          <CardContent className="pt-6 px-6 pb-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-bold text-slate-400 tracking-wider">Buddy Programs</p>
                <p className="text-3xl font-bold text-purple-600 mt-2">{activeBuddyPrograms}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-purple-50 dark:bg-purple-950/30 flex items-center justify-center shrink-0 shadow-sm border border-purple-100/30">
                <Users className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={selectedTab} onValueChange={setSelectedTab} className="space-y-6">
        <TabsList className="bg-white/50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 p-1 mb-2 w-fit shadow-sm backdrop-blur-sm shrink-0 flex h-auto">
          <TabsTrigger value="dashboard" className="px-6 py-2 text-sm font-bold data-[state=active]:bg-[#143A62] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600 transition-all rounded-md shadow-none">Dashboard</TabsTrigger>
          <TabsTrigger value="onboarding" className="px-6 py-2 text-sm font-bold data-[state=active]:bg-[#143A62] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600 transition-all rounded-md shadow-none">Onboarding</TabsTrigger>
          <TabsTrigger value="offboarding" className="px-6 py-2 text-sm font-bold data-[state=active]:bg-[#143A62] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600 transition-all rounded-md shadow-none">Offboarding</TabsTrigger>
          <TabsTrigger value="buddy" className="px-6 py-2 text-sm font-bold data-[state=active]:bg-[#143A62] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600 transition-all rounded-md shadow-none">Buddy Program</TabsTrigger>
        </TabsList>

        {/* Dashboard */}
        <TabsContent value="dashboard" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border border-slate-100 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 rounded-2xl overflow-hidden">
              <CardHeader className="pb-4 pt-5 px-6 border-b border-slate-50 dark:border-slate-800/50">
                <CardTitle className="text-lg font-bold text-[#143A62] dark:text-slate-100 tracking-tight">Recent Onboarding Activities</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4.5">
                  {onboardingList.length === 0 ? (
                    <div className="py-8 text-center text-slate-400">
                      <p className="text-xs font-bold">No onboarding processes yet</p>
                    </div>
                  ) : (
                    onboardingList.slice(0, 5).map((process: any) => (
                      <div 
                        key={process.id} 
                        className="flex items-center justify-between p-4 border border-slate-100 dark:border-slate-800/60 rounded-xl cursor-pointer hover:bg-slate-50/50 dark:hover:bg-slate-800/20 transition-colors shadow-sm" 
                        onClick={() => handleViewProcess(process)}
                      >
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 bg-blue-50 dark:bg-blue-900/20 border border-blue-100/35 rounded-xl flex items-center justify-center shrink-0">
                            <UserPlus className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div>
                            <p className="font-bold text-xs text-[#143A62] dark:text-slate-200">{getEmployeeName(process.employee_id)}</p>
                            <p className="text-[10px] font-bold text-slate-400 tracking-wide mt-0.5">{process.current_stage || 'Not started'}</p>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <Badge className={cn("px-2 py-0.5 rounded-lg font-bold text-[9px] tracking-wide border shadow-none shrink-0 capitalize", getStatusColor(process.status))} variant="outline">
                            {process.status?.replace('_', ' ').toLowerCase()}
                          </Badge>
                          <p className="text-[10px] font-extrabold text-slate-600 dark:text-slate-400 mt-1">{getDisplayProgress(process)}%</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="border border-slate-100 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 rounded-2xl overflow-hidden">
              <CardHeader className="pb-4 pt-5 px-6 border-b border-slate-50 dark:border-slate-800/50">
                <CardTitle className="text-lg font-bold text-[#143A62] dark:text-slate-100 tracking-tight">Recent Offboarding Activities</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4.5">
                  {offboardingList.length === 0 ? (
                    <div className="py-8 text-center text-slate-400">
                      <p className="text-xs font-bold">No offboarding processes yet</p>
                    </div>
                  ) : (
                    offboardingList.slice(0, 5).map((process: any) => (
                      <div 
                        key={process.id} 
                        className="flex items-center justify-between p-4 border border-slate-100 dark:border-slate-800/60 rounded-xl cursor-pointer hover:bg-slate-50/50 dark:hover:bg-slate-800/20 transition-colors shadow-sm" 
                        onClick={() => handleViewProcess(process)}
                      >
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 bg-orange-50 dark:bg-orange-900/20 border border-orange-100/35 rounded-xl flex items-center justify-center shrink-0">
                            <UserMinus className="h-5 w-5 text-orange-600" />
                          </div>
                          <div>
                            <p className="font-bold text-xs text-[#143A62] dark:text-slate-200">{getEmployeeName(process.employee_id)}</p>
                            <p className="text-[10px] font-bold text-slate-400 tracking-wide mt-0.5">{!isUserView && canViewOffboardingDetails ? (process.reason || 'Resignation') : 'Offboarding'}</p>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <Badge className={cn("px-2 py-0.5 rounded-lg font-bold text-[9px] tracking-wide border shadow-none shrink-0 capitalize", getStatusColor(process.status))} variant="outline">
                            {process.status?.replace('_', ' ').toLowerCase()}
                          </Badge>
                          <p className="text-[10px] font-bold text-slate-500 mt-1">
                            {!isUserView && canViewOffboardingDetails ? (process.last_working_day || 'TBD') : ''}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Onboarding */}
        <TabsContent value="onboarding" className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">Onboarding Processes</h3>
            {!isUserView && canDoOnboardingActions && (
              <Button
                onClick={() => { setEditingProcess(null); setOnboardingForm(initialOnboardingForm); setIsOnboardingDialogOpen(true); }}
              >
                <Plus className="h-4 w-4 shrink-0 mr-1.5" />
                <span>New Process</span>
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 gap-6">
            {onboardingList.length === 0 ? (
              <Card className="border border-slate-100 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 rounded-2xl overflow-hidden">
                <CardContent className="py-16 text-center text-slate-400">
                  <UserPlus className="h-12 w-12 mx-auto mb-4 opacity-40 animate-pulse text-blue-500" />
                  <p className="font-bold text-sm text-[#143A62] dark:text-slate-200">No onboarding processes</p>
                  <p className="text-xs font-semibold text-slate-400 mt-2">Click "New Process" to start onboarding a new employee</p>
                </CardContent>
              </Card>
            ) : (
              onboardingList.map((process: any) => (
                <Card key={process.id} className="border border-slate-105 dark:border-slate-800/80 shadow-md bg-white dark:bg-slate-900 rounded-2xl overflow-hidden">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-5">
                      <div className="flex items-center gap-4">
                        <div className="h-12 w-12 bg-blue-50 dark:bg-blue-900/20 border border-blue-100/30 rounded-xl flex items-center justify-center shrink-0 shadow-sm">
                          <UserPlus className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                          <h3 className="font-extrabold text-base text-[#143A62] dark:text-slate-100">{getEmployeeName(process.employee_id)}</h3>
                          <p className="text-[10px] font-bold text-slate-400 tracking-wide mt-1 uppercase">Started: {process.start_date || 'N/A'}</p>
                        </div>
                      </div>
                      <Badge className={cn("px-2.5 py-0.5 rounded-lg font-bold text-[10px] tracking-wide border shadow-none shrink-0 capitalize", getStatusColor(process.status))} variant="outline">
                        {process.status?.replace('_', ' ').toLowerCase()}
                      </Badge>
                    </div>

                    <div className="space-y-5">
                      <div className="bg-slate-50/40 dark:bg-slate-800/10 p-3.5 rounded-xl border border-slate-100 dark:border-slate-800/40">
                        <div className="flex justify-between mb-2">
                          <span className="text-xs font-bold text-slate-400 tracking-wider uppercase">Progress</span>
                          <span className="text-xs font-extrabold text-slate-600 dark:text-slate-400">{getDisplayProgress(process)}%</span>
                        </div>
                        <Progress value={getDisplayProgress(process)} className="h-2 rounded-full" />
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                        <div>
                          <span className="text-[10px] font-bold text-slate-400 tracking-wide uppercase block">Current Stage</span>
                          <p className="font-bold text-[#143A62] mt-1 dark:text-slate-200">{process.current_stage || 'Not started'}</p>
                        </div>
                        <div>
                          <span className="text-[10px] font-bold text-slate-400 tracking-wide uppercase block">Expected Completion</span>
                          <p className="font-bold text-[#143A62] mt-1 dark:text-slate-200">{process.expected_completion_date || 'N/A'}</p>
                        </div>
                        <div>
                          <span className="text-[10px] font-bold text-slate-400 tracking-wide uppercase block">Assigned To</span>
                          <p className="font-bold text-[#143A62] mt-1 dark:text-slate-200">{process.assigned_to || 'Unassigned'}</p>
                        </div>
                        <div>
                          <span className="text-[10px] font-bold text-slate-400 tracking-wide uppercase block">Buddy</span>
                          <p className="font-bold text-indigo-600 mt-1 dark:text-indigo-400">{process.buddy_id ? getEmployeeName(process.buddy_id) : 'Not assigned'}</p>
                        </div>
                      </div>

                      <div className="flex justify-between items-center pt-3 border-t border-slate-100 dark:border-slate-800/80">
                        <div className="text-[10px] font-bold text-slate-400 tracking-wide">
                          Last updated: {process.updated_at ? new Date(process.updated_at).toLocaleDateString() : 'N/A'}
                        </div>
                        <div className="flex items-center space-x-1.5">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleViewProcess(process)}
                          >
                            <Eye className="h-3.5 w-3.5 mr-1 shrink-0" />
                            View
                          </Button>
                          {!isUserView && canDoOnboardingActions && (
                            <>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => handleEditProcess(process)}
                              >
                                <Edit className="h-3.5 w-3.5 mr-1 shrink-0" />
                                Edit
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                onClick={() => handleDeleteProcess(process.id)}
                              >
                                <Trash2 className="h-3.5 w-3.5 shrink-0 text-rose-500" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Offboarding */}
        <TabsContent value="offboarding" className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">Offboarding Processes</h3>
            {!isUserView && canDoOnboardingActions && (
              <Button
                variant="outline"
                onClick={() => { setEditingProcess(null); setOnboardingForm({ ...initialOnboardingForm, process_type: 'OFFBOARDING' }); setIsOffboardingDialogOpen(true); }}
              >
                <Plus className="h-4 w-4 shrink-0 mr-1.5" />
                <span>Start Offboarding</span>
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 gap-6">
            {offboardingList.length === 0 ? (
              <Card className="border border-slate-100 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 rounded-2xl overflow-hidden">
                <CardContent className="py-16 text-center text-slate-400">
                  <UserMinus className="h-12 w-12 mx-auto mb-4 opacity-40 animate-pulse text-orange-500" />
                  <p className="font-bold text-sm text-[#143A62] dark:text-slate-200">No offboarding processes</p>
                  <p className="text-xs font-semibold text-slate-400 mt-2">Click "Start Offboarding" to initiate an offboarding process</p>
                </CardContent>
              </Card>
            ) : (
              offboardingList.map((process: any) => (
                <Card key={process.id} className="border border-slate-105 dark:border-slate-800/80 shadow-md bg-white dark:bg-slate-900 rounded-2xl overflow-hidden">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-5">
                      <div className="flex items-center gap-4">
                        <div className="h-12 w-12 bg-orange-50 dark:bg-orange-900/20 border border-orange-100/30 rounded-xl flex items-center justify-center shrink-0 shadow-sm">
                          <UserMinus className="h-6 w-6 text-orange-600" />
                        </div>
                        <div>
                          <h3 className="font-extrabold text-base text-[#143A62] dark:text-slate-100">{getEmployeeName(process.employee_id)}</h3>
                          {!isUserView && canViewOffboardingDetails && <p className="text-xs font-bold text-slate-400 mt-1 uppercase">Reason: {process.reason || 'Resignation'}</p>}
                        </div>
                      </div>
                      <Badge className={cn("px-2.5 py-0.5 rounded-lg font-bold text-[10px] tracking-wide border shadow-none shrink-0 capitalize", getStatusColor(process.status))} variant="outline">
                        {process.status?.replace('_', ' ').toLowerCase()}
                      </Badge>
                    </div>

                    <div className="space-y-5">
                      <div className="bg-slate-50/40 dark:bg-slate-800/10 p-3.5 rounded-xl border border-slate-100 dark:border-slate-800/40">
                        <div className="flex justify-between mb-2">
                          <span className="text-xs font-bold text-slate-400 tracking-wider uppercase">Progress</span>
                          <span className="text-xs font-extrabold text-slate-600 dark:text-slate-400">{getDisplayProgress(process)}%</span>
                        </div>
                        <Progress value={getDisplayProgress(process)} className="h-2 rounded-full" />
                      </div>

                      <div className="grid grid-cols-2 gap-4 text-xs">
                        <div>
                          <span className="text-[10px] font-bold text-slate-400 tracking-wide uppercase block">Current Stage</span>
                          <p className="font-bold text-[#143A62] mt-1 dark:text-slate-200">{process.current_stage || 'Not started'}</p>
                        </div>
                        {!isUserView && canViewOffboardingDetails && (
                          <div>
                            <span className="text-[10px] font-bold text-slate-400 tracking-wide uppercase block">Last Working Day</span>
                            <p className="font-bold text-[#143A62] mt-1 dark:text-slate-200">{process.last_working_day || 'TBD'}</p>
                          </div>
                        )}
                      </div>

                      <div className="flex justify-between items-center pt-3 border-t border-slate-100 dark:border-slate-800/80">
                        <div className="text-[10px] font-bold text-slate-400 tracking-wide">
                          Initiated: {process.created_at ? new Date(process.created_at).toLocaleDateString() : 'N/A'}
                        </div>
                        <div className="flex items-center space-x-1.5">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleViewProcess(process)}
                          >
                            <Eye className="h-3.5 w-3.5 mr-1 shrink-0" />
                            View
                          </Button>
                          {!isUserView && canDoOnboardingActions && (
                            <>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => handleEditProcess(process)}
                              >
                                <Edit className="h-3.5 w-3.5 mr-1 shrink-0" />
                                Edit
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                onClick={() => handleDeleteProcess(process.id)}
                              >
                                <Trash2 className="h-3.5 w-3.5 shrink-0 text-rose-500" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Buddy Program */}
        <TabsContent value="buddy" className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">Buddy Programs</h3>
            {!isUserView && canDoOnboardingActions && (
              <Button
                onClick={() => { setEditingProcess(null); setBuddyForm(initialBuddyForm); setIsBuddyDialogOpen(true); }}
              >
                <Plus className="h-4 w-4 shrink-0 mr-1.5" />
                <span>Assign Buddy</span>
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 gap-6">
            {buddyPrograms.length === 0 ? (
              <Card className="border border-slate-100 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 rounded-2xl overflow-hidden">
                <CardContent className="py-16 text-center text-slate-400">
                  <Users className="h-12 w-12 mx-auto mb-4 opacity-40 animate-pulse text-purple-500" />
                  <p className="font-bold text-sm text-[#143A62] dark:text-slate-200">No buddy programs</p>
                  <p className="text-xs font-semibold text-slate-400 mt-2">Click "Assign Buddy" to pair a new employee with a buddy</p>
                </CardContent>
              </Card>
            ) : (
              buddyPrograms.map((program: any) => (
                <Card key={program.id} className="border border-slate-105 dark:border-slate-800/80 shadow-md bg-white dark:bg-slate-900 rounded-2xl overflow-hidden">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-5">
                      <div className="flex items-center gap-4">
                        <div className="h-12 w-12 bg-purple-50 dark:bg-purple-900/20 border border-purple-100/35 rounded-xl flex items-center justify-center shrink-0 shadow-sm">
                          <Users className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                        </div>
                        <div>
                          <h3 className="font-extrabold text-base text-[#143A62] dark:text-slate-100">
                            {getEmployeeName(program.employee_id)} & {getEmployeeName(program.buddy_id)}
                          </h3>
                          <p className="text-[10px] font-bold text-slate-400 mt-1 uppercase">Started: {program.start_date || 'N/A'}</p>
                        </div>
                      </div>
                      <Badge className={cn("px-2.5 py-0.5 rounded-lg font-bold text-[10px] tracking-wide border shadow-none shrink-0 capitalize", getStatusColor(program.status))} variant="outline">
                        {program.status?.replace('_', ' ').toLowerCase()}
                      </Badge>
                    </div>

                    <div className="space-y-5">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-slate-50/40 dark:bg-slate-800/10 p-4.5 rounded-xl border border-slate-100 dark:border-slate-800/40 text-xs">
                        <div>
                          <span className="text-[10px] font-bold text-slate-400 tracking-wide uppercase block">New Employee</span>
                          <p className="font-bold text-[#143A62] mt-1 dark:text-slate-200">{getEmployeeName(program.employee_id)}</p>
                        </div>
                        <div>
                          <span className="text-[10px] font-bold text-slate-400 tracking-wide uppercase block">Assigned Buddy</span>
                          <p className="font-bold text-indigo-600 mt-1 dark:text-indigo-400">{getEmployeeName(program.buddy_id)}</p>
                        </div>
                      </div>

                      {program.notes && (
                        <div className="bg-slate-50/20 dark:bg-slate-800/5 p-4 rounded-xl border border-slate-100/50 dark:border-slate-800/30">
                          <p className="text-xs font-semibold text-slate-600 dark:text-slate-400 leading-relaxed whitespace-pre-wrap">{program.notes}</p>
                        </div>
                      )}

                      <div className="flex justify-between items-center pt-3 border-t border-slate-100 dark:border-slate-800/85">
                        <div className="text-[10px] font-extrabold text-slate-400 tracking-wide uppercase">
                          Progress: {getDisplayProgress(program)}%
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleViewBuddy(program)}
                          >
                            <Eye className="h-3.5 w-3.5 mr-1 shrink-0" />
                            View Details
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleEditBuddy(program)}
                          >
                            <Edit className="h-3.5 w-3.5 mr-1 shrink-0" />
                            Edit
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleFeedback(program)}
                          >
                            <Star className="h-3.5 w-3.5 mr-1 shrink-0" />
                            Feedback
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Onboarding Dialog */}
      <Dialog open={isOnboardingDialogOpen} onOpenChange={setIsOnboardingDialogOpen}>
        <DialogContent className="w-[min(96vw,500px)] max-w-[500px] max-h-[92vh] flex flex-col rounded-2xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 p-0 overflow-hidden shadow-2xl gap-0">
          <DialogHeader className="p-6 pb-4 border-b border-slate-100 dark:border-slate-800 shrink-0">
            <DialogTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100">{editingProcess ? 'Edit' : 'Start'} Onboarding</DialogTitle>
            <DialogDescription className="text-xs font-semibold text-slate-400 mt-1 tracking-wider">
              Create a new onboarding process for an employee
            </DialogDescription>
          </DialogHeader>
          
          <div className="p-6 space-y-4 overflow-y-auto flex-1 custom-scrollbar">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold text-slate-400 tracking-wider">Employee *</Label>
              <Select value={onboardingForm.employee_id} onValueChange={(value) => setOnboardingForm({ ...onboardingForm, employee_id: value })}>
                <SelectTrigger className="rounded-xl border-slate-200 dark:border-slate-800 font-bold text-xs h-10 shadow-sm">
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  {employees.map((emp: any) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-slate-400 tracking-wider">Start Date</Label>
                <Input 
                  type="date" 
                  value={onboardingForm.start_date} 
                  onChange={(e) => setOnboardingForm({ ...onboardingForm, start_date: e.target.value })} 
                  className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-xs h-10 shadow-sm"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-slate-400 tracking-wider">Expected Completion *</Label>
                <Input 
                  type="date" 
                  value={onboardingForm.expected_completion_date} 
                  onChange={(e) => setOnboardingForm({ ...onboardingForm, expected_completion_date: e.target.value })} 
                  className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-xs h-10 shadow-sm"
                />
              </div>
            </div>
            
            <div className="space-y-1.5">
              <Label className="text-xs font-bold text-slate-400 tracking-wider">Status</Label>
              <Select value={onboardingForm.status} onValueChange={(value) => setOnboardingForm({ ...onboardingForm, status: value })}>
                <SelectTrigger className="rounded-xl border-slate-200 dark:border-slate-800 font-bold text-xs h-10 shadow-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="NOT_STARTED">Not Started</SelectItem>
                  <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                  <SelectItem value="COMPLETED">Completed</SelectItem>
                  <SelectItem value="DELAYED">Delayed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-1.5">
              <Label className="text-xs font-bold text-slate-400 tracking-wider">Current Stage</Label>
              <Select value={onboardingForm.current_stage} onValueChange={(value) => setOnboardingForm({ ...onboardingForm, current_stage: value })}>
                <SelectTrigger className="rounded-xl border-slate-200 dark:border-slate-800 font-bold text-xs h-10 shadow-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="Documentation">Documentation</SelectItem>
                  <SelectItem value="IT Setup">IT Setup</SelectItem>
                  <SelectItem value="Training">Training</SelectItem>
                  <SelectItem value="Team Introduction">Team Introduction</SelectItem>
                  <SelectItem value="Buddy Program">Buddy Program</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-1.5">
              <Label className="text-xs font-bold text-slate-400 tracking-wider">Buddy (Optional)</Label>
              <Select value={onboardingForm.buddy_id} onValueChange={(value) => setOnboardingForm({ ...onboardingForm, buddy_id: value })}>
                <SelectTrigger className="rounded-xl border-slate-200 dark:border-slate-800 font-bold text-xs h-10 shadow-sm">
                  <SelectValue placeholder="Select buddy" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  {employees.filter((emp: any) => emp.id !== onboardingForm.employee_id).map((emp: any) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-1.5">
              <Label className="text-xs font-bold text-slate-400 tracking-wider">Notes</Label>
              <Textarea 
                value={onboardingForm.notes} 
                onChange={(e) => setOnboardingForm({ ...onboardingForm, notes: e.target.value })} 
                rows={2} 
                className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-semibold text-xs shadow-sm"
              />
            </div>
          </div>
          
          <DialogFooter className="p-6 pt-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/10 shrink-0 flex gap-2">
            <Button variant="outline" onClick={() => { setIsOnboardingDialogOpen(false); setEditingProcess(null); }}>Cancel</Button>
            <Button onClick={editingProcess ? handleUpdateProcess : handleCreateOnboarding}>
              {editingProcess ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Offboarding Dialog */}
      <Dialog open={isOffboardingDialogOpen} onOpenChange={setIsOffboardingDialogOpen}>
        <DialogContent className="w-[min(96vw,500px)] max-w-[500px] max-h-[92vh] flex flex-col rounded-2xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 p-0 overflow-hidden shadow-2xl gap-0">
          <DialogHeader className="p-6 pb-4 border-b border-slate-100 dark:border-slate-800 shrink-0">
            <DialogTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100">{editingProcess ? 'Edit' : 'Start'} Offboarding</DialogTitle>
            <DialogDescription className="text-xs font-semibold text-slate-400 mt-1 tracking-wider">
              Initiate an offboarding process for an employee
            </DialogDescription>
          </DialogHeader>
          
          <div className="p-6 space-y-4 overflow-y-auto flex-1 custom-scrollbar">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold text-slate-400 tracking-wider">Employee *</Label>
              <Select value={onboardingForm.employee_id} onValueChange={(value) => setOnboardingForm({ ...onboardingForm, employee_id: value })}>
                <SelectTrigger className="rounded-xl border-slate-200 dark:border-slate-800 font-bold text-xs h-10 shadow-sm">
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  {employees.map((emp: any) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-1.5">
              <Label className="text-xs font-bold text-slate-400 tracking-wider">Reason</Label>
              <Select value={onboardingForm.reason} onValueChange={(value) => setOnboardingForm({ ...onboardingForm, reason: value })}>
                <SelectTrigger className="rounded-xl border-slate-200 dark:border-slate-800 font-bold text-xs h-10 shadow-sm">
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="Resignation">Resignation</SelectItem>
                  <SelectItem value="Termination">Termination</SelectItem>
                  <SelectItem value="Retirement">Retirement</SelectItem>
                  <SelectItem value="Contract End">Contract End</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-1.5">
              <Label className="text-xs font-bold text-slate-400 tracking-wider">Last Working Day *</Label>
              <Input 
                type="date" 
                value={onboardingForm.last_working_day} 
                onChange={(e) => setOnboardingForm({ ...onboardingForm, last_working_day: e.target.value })} 
                className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-xs h-10 shadow-sm"
              />
            </div>
            
            <div className="space-y-1.5">
              <Label className="text-xs font-bold text-slate-400 tracking-wider">Status</Label>
              <Select value={onboardingForm.status} onValueChange={(value) => setOnboardingForm({ ...onboardingForm, status: value })}>
                <SelectTrigger className="rounded-xl border-slate-200 dark:border-slate-800 font-bold text-xs h-10 shadow-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="NOT_STARTED">Not Started</SelectItem>
                  <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                  <SelectItem value="COMPLETED">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-1.5">
              <Label className="text-xs font-bold text-slate-400 tracking-wider">Notes</Label>
              <Textarea 
                value={onboardingForm.notes} 
                onChange={(e) => setOnboardingForm({ ...onboardingForm, notes: e.target.value })} 
                rows={2} 
                placeholder="Handover notes, etc." 
                className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-semibold text-xs shadow-sm"
              />
            </div>
          </div>
          
          <DialogFooter className="p-6 pt-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/10 shrink-0 flex gap-2">
            <Button variant="outline" onClick={() => { setIsOffboardingDialogOpen(false); setEditingProcess(null); }}>Cancel</Button>
            <Button onClick={editingProcess ? handleUpdateProcess : handleCreateOffboarding}>
              {editingProcess ? 'Update' : 'Start Offboarding'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Buddy Assignment Dialog */}
      <Dialog open={isBuddyDialogOpen} onOpenChange={setIsBuddyDialogOpen}>
        <DialogContent className="w-[min(96vw,500px)] max-w-[500px] max-h-[92vh] flex flex-col rounded-2xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 p-0 overflow-hidden shadow-2xl gap-0">
          <DialogHeader className="p-6 pb-4 border-b border-slate-100 dark:border-slate-800 shrink-0">
            <DialogTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100">{editingProcess ? 'Edit Buddy Assignment' : 'Assign Buddy'}</DialogTitle>
            <DialogDescription className="text-xs font-semibold text-slate-400 mt-1 tracking-wider">
              {editingProcess ? 'Update buddy assignment details' : 'Pair a new employee with an experienced buddy'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="p-6 space-y-4 overflow-y-auto flex-1 custom-scrollbar">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold text-slate-400 tracking-wider">New Employee *</Label>
              <Select disabled={!!editingProcess} value={buddyForm.onboardee_id} onValueChange={(value) => setBuddyForm({ ...buddyForm, onboardee_id: value })}>
                <SelectTrigger className="rounded-xl border-slate-200 dark:border-slate-800 font-bold text-xs h-10 shadow-sm">
                  <SelectValue placeholder="Select new employee" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  {employees.filter((emp: any) => emp.id !== buddyForm.buddy_id).map((emp: any) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-1.5">
              <Label className="text-xs font-bold text-slate-400 tracking-wider">Buddy *</Label>
              <Select value={buddyForm.buddy_id} onValueChange={(value) => setBuddyForm({ ...buddyForm, buddy_id: value })}>
                <SelectTrigger className="rounded-xl border-slate-200 dark:border-slate-800 font-bold text-xs h-10 shadow-sm">
                  <SelectValue placeholder="Select buddy" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  {employees.filter((emp: any) => emp.id !== buddyForm.onboardee_id).map((emp: any) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-1.5">
              <Label className="text-xs font-bold text-slate-400 tracking-wider">Duration (days)</Label>
              <Input 
                type="number" 
                value={buddyForm.duration} 
                onChange={(e) => setBuddyForm({ ...buddyForm, duration: parseInt(e.target.value) || 30 })} 
                className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-xs h-10 shadow-sm"
              />
            </div>
            
            <div className="space-y-1.5">
              <Label className="text-xs font-bold text-slate-400 tracking-wider">Notes</Label>
              <Textarea 
                value={buddyForm.notes} 
                onChange={(e) => setBuddyForm({ ...buddyForm, notes: e.target.value })} 
                rows={2} 
                className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-semibold text-xs shadow-sm"
              />
            </div>
          </div>
          
          <DialogFooter className="p-6 pt-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/10 shrink-0 flex gap-2">
            <Button variant="outline" onClick={() => setIsBuddyDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAssignBuddy}>{editingProcess ? 'Update Buddy' : 'Assign Buddy'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Process Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="w-[min(96vw,500px)] max-w-[500px] max-h-[92vh] flex flex-col rounded-2xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 p-0 overflow-hidden shadow-2xl gap-0 animate-in fade-in zoom-in-95 duration-200">
          <DialogHeader className="p-6 pb-4 border-b border-slate-100 dark:border-slate-800 shrink-0">
            <DialogTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100">Process Details</DialogTitle>
          </DialogHeader>
          
          {(viewingProcess || viewingBuddy) && (
            <div className="p-6 space-y-5 overflow-y-auto flex-1 custom-scrollbar">
              <div className="grid grid-cols-2 gap-6 bg-slate-50/50 dark:bg-slate-800/10 p-4.5 rounded-2xl border border-slate-100 dark:border-slate-800">
                <div>
                  <Label className="text-[10px] font-bold text-slate-400 tracking-wider uppercase">Employee</Label>
                  <p className="font-bold text-xs text-slate-700 mt-1 dark:text-slate-200">{getEmployeeName((viewingProcess || viewingBuddy).employee_id)}</p>
                </div>
                <div>
                  <Label className="text-[10px] font-bold text-slate-400 tracking-wider uppercase">Status</Label>
                  <div className="mt-1">
                    <Badge className={cn("px-2.5 py-0.5 rounded-lg font-bold text-[10px] tracking-wide border shadow-none shrink-0 capitalize", getStatusColor((viewingProcess || viewingBuddy).status))} variant="outline">
                      {(viewingProcess || viewingBuddy).status?.replace('_', ' ').toLowerCase()}
                    </Badge>
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] font-bold text-slate-400 tracking-wider uppercase">Start Date</Label>
                  <p className="font-bold text-xs text-slate-700 mt-1 dark:text-slate-200">{(viewingProcess || viewingBuddy).start_date || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-[10px] font-bold text-slate-400 tracking-wider uppercase">Progress</Label>
                  <p className="font-bold text-xs text-slate-700 mt-1 dark:text-slate-200">{(viewingProcess || viewingBuddy).completion_percentage || 0}%</p>
                </div>
              </div>
              
              {(viewingProcess || viewingBuddy).buddy_id && (
                <div className="space-y-1">
                  <Label className="text-[10px] font-bold text-slate-400 tracking-wider uppercase">Assigned Buddy</Label>
                  <p className="font-bold text-xs text-slate-700 dark:text-slate-200">{getEmployeeName((viewingProcess || viewingBuddy).buddy_id)}</p>
                </div>
              )}
              
              {(viewingProcess || viewingBuddy).notes && (
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-bold text-slate-400 tracking-wider uppercase">Notes</Label>
                  <p className="text-xs font-semibold text-slate-500 leading-relaxed bg-slate-50/20 dark:bg-slate-800/5 p-3 rounded-xl border border-slate-100/50 dark:border-slate-800/30 whitespace-pre-wrap">{(viewingProcess || viewingBuddy).notes}</p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="p-6 pt-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/10 shrink-0 flex gap-2">
            <Button variant="outline" onClick={() => { setIsViewDialogOpen(false); setViewingProcess(null); setViewingBuddy(null); }}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Feedback Dialog */}
      <Dialog open={isFeedbackDialogOpen} onOpenChange={setIsFeedbackDialogOpen}>
        <DialogContent className="w-[min(96vw,500px)] max-w-[500px] max-h-[92vh] flex flex-col rounded-2xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 p-0 overflow-hidden shadow-2xl gap-0">
          <DialogHeader className="p-6 pb-4 border-b border-slate-100 dark:border-slate-800 shrink-0">
            <DialogTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100">Submit Feedback</DialogTitle>
            <DialogDescription className="text-xs font-semibold text-slate-400 mt-1 tracking-wider">
              Provide feedback for the buddy program
            </DialogDescription>
          </DialogHeader>
          
          <div className="p-6 space-y-4 overflow-y-auto flex-1 custom-scrollbar">
            <div className="space-y-2">
              <Label className="text-xs font-bold text-slate-400 tracking-wider">Rating</Label>
              <div className="flex gap-2.5 mt-1.5">
                {[1, 2, 3, 4, 5].map((rating) => (
                  <Button
                    key={rating}
                    variant={feedbackRating >= rating ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFeedbackRating(rating)}
                    className={cn(
                      "h-10 w-10 p-0 rounded-xl transition-all shadow-none shrink-0",
                      feedbackRating >= rating 
                        ? 'bg-amber-500 hover:bg-amber-600 text-white border-amber-600' 
                        : 'border-slate-200 text-slate-400 hover:bg-slate-50'
                    )}
                  >
                    <Star className={cn("h-4.5 w-4.5 shrink-0", feedbackRating >= rating ? 'fill-current' : '')} />
                  </Button>
                ))}
              </div>
            </div>
            
            <div className="space-y-1.5">
              <Label className="text-xs font-bold text-slate-400 tracking-wider">Feedback</Label>
              <Textarea
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                rows={4}
                placeholder="Share your experience with the buddy program..."
                className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-semibold text-xs shadow-sm"
              />
            </div>
          </div>
          
          <DialogFooter className="p-6 pt-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/10 shrink-0 flex gap-2">
            <Button variant="outline" onClick={() => setIsFeedbackDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmitFeedback}>Submit Feedback</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OnboardingOffboarding;
